// Developer: Nithin | Date: 2026-07-26 | New ShiftModal component for assigning shifts to employees
// Developer: Nithin | Date: 2026-07-26 | Added existing-shift validation — auto-fills form and switches to Update mode if employee already has a shift
import React, { useState, useMemo, useEffect } from "react";
import { X, Clock, Search, ChevronDown, AlertCircle, RefreshCw } from "lucide-react";
import { useHR } from "../../context/HRContext";
import toast from "react-hot-toast";

const SHIFT_OPTIONS = [
  { value: "morning", label: "Morning", icon: "🌅", desc: "Early shift" },
  { value: "night",   label: "Night",   icon: "🌙", desc: "Late shift"  },
  { value: "other",   label: "Other",   icon: "🕐", desc: "Custom shift" },
];

const EMPTY_FORM = {
  employeeId: "",
  employeeName: "",
  shift: "morning",
  startTime: "",
  endTime: "",
  notes: "",
};

// Format "HH:MM" → "9:00 AM / 6:00 PM" for display in banner
const formatTime = (t) => {
  if (!t) return "—";
  const [hStr, mStr] = t.split(":");
  let h = parseInt(hStr, 10);
  const m = mStr || "00";
  const ampm = h >= 12 ? "PM" : "AM";
  h = h % 12 || 12;
  return `${h}:${m} ${ampm}`;
};

export function ShiftModal({ isOpen, onClose }) {
  const { employees, shifts, addShift, editShift } = useHR();

  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [empSearch, setEmpSearch] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState(false);

  // Existing shift for the currently selected employee (first match)
  const existingShift = useMemo(() => {
    if (!form.employeeId) return null;
    return (shifts || []).find((s) => s.employeeId === form.employeeId) || null;
  }, [shifts, form.employeeId]);

  const isUpdateMode = Boolean(existingShift);

  // When an employee with an existing shift is selected, pre-fill the form
  useEffect(() => {
    if (existingShift) {
      setForm((p) => ({
        ...p,
        shift:     existingShift.shift     || "morning",
        startTime: existingShift.startTime || "",
        endTime:   existingShift.endTime   || "",
        notes:     existingShift.notes     || "",
      }));
      setErrors({});
    }
  }, [existingShift]);

  // Non-owner employees for selection
  const empOptions = useMemo(() => {
    const q = empSearch.toLowerCase().trim();
    return employees.filter((e) => {
      if (!q) return true;
      return (
        e.name?.toLowerCase().includes(q) ||
        e.employeeId?.toLowerCase().includes(q) ||
        (e.id && String(e.id).toLowerCase().includes(q))
      );
    });
  }, [employees, empSearch]);

  const setField = (key, value) => {
    setForm((p) => ({ ...p, [key]: value }));
    setErrors((p) => ({ ...p, [key]: undefined }));
  };

  const handleSelectEmployee = (emp) => {
    // Reset time fields first so useEffect can re-fill cleanly
    setForm({
      ...EMPTY_FORM,
      employeeId:   emp.employeeId || emp.id,
      employeeName: emp.name || "",
    });
    setErrors({});
    setEmpSearch("");
    setDropdownOpen(false);
  };

  const validate = () => {
    const errs = {};
    if (!form.employeeId) errs.employeeId = "Please select an employee";
    if (!form.shift)      errs.shift      = "Please select a shift";
    if (!form.startTime)  errs.startTime  = "Start time is required";
    if (!form.endTime)    errs.endTime    = "End time is required";
    if (
      form.startTime &&
      form.endTime &&
      form.shift !== "night" &&
      form.startTime >= form.endTime
    ) {
      errs.endTime = "End time must be after start time";
    }
    return errs;
  };

  const handleSubmit = async () => {
    const errs = validate();
    if (Object.keys(errs).length) {
      setErrors(errs);
      toast.error(Object.values(errs)[0]);
      return;
    }
    setIsSaving(true);
    try {
      const payload = {
        employeeId:   form.employeeId,
        employeeName: form.employeeName,
        shift:        form.shift,
        startTime:    form.startTime,
        endTime:      form.endTime,
        notes:        form.notes.trim(),
      };

      if (isUpdateMode) {
        // Update existing shift via PUT
        await editShift(existingShift._id || existingShift.id, payload);
        toast.success(`Shift updated for ${form.employeeName}`);
      } else {
        // Create new shift via POST
        await addShift(payload);
        toast.success(`Shift assigned to ${form.employeeName}`);
      }

      setForm(EMPTY_FORM);
      setErrors({});
      onClose();
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || "Failed to save shift";
      toast.error(msg);
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    setForm(EMPTY_FORM);
    setErrors({});
    setEmpSearch("");
    setDropdownOpen(false);
    onClose();
  };

  if (!isOpen) return null;

  const shiftEmoji = existingShift?.shift === "morning" ? "🌅" : existingShift?.shift === "night" ? "🌙" : "🕐";

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={handleClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg animate-in fade-in zoom-in-95 duration-200 overflow-hidden flex flex-col max-h-[90vh]">

        {/* Header */}
        <div className="bg-gradient-to-br from-[#1d4ed8] to-[#1e3a8a] px-6 py-5 text-white flex-shrink-0">
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 p-1.5 rounded-lg hover:bg-white/20 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
              {isUpdateMode ? <RefreshCw className="w-5 h-5 text-white" /> : <Clock className="w-5 h-5 text-white" />}
            </div>
            <div>
              <h2 className="text-[16px] font-bold">
                {isUpdateMode ? "Update Shift" : "Assign Shift"}
              </h2>
              <p className="text-[12px] text-blue-200 mt-0.5">
                {isUpdateMode
                  ? `Updating existing shift for ${form.employeeName}`
                  : "Assign a work shift to an employee"}
              </p>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-5">

          {/* Employee Search Dropdown */}
          <div className="relative">
            <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5 block">
              Employee <span className="text-red-500">*</span>
            </label>
            <button
              type="button"
              onClick={() => setDropdownOpen((v) => !v)}
              className={`w-full flex items-center justify-between gap-2 px-3 py-2.5 rounded-lg border text-sm transition-all ${
                errors.employeeId
                  ? "border-red-400 bg-red-50"
                  : "border-slate-200 bg-white hover:border-blue-400"
              } focus:outline-none`}
            >
              <span className={form.employeeName ? "text-slate-800 font-medium" : "text-slate-400"}>
                {form.employeeName
                  ? `${form.employeeName}${form.employeeId ? ` — ${form.employeeId}` : ""}`
                  : "Search and select employee..."}
              </span>
              <ChevronDown
                size={15}
                className={`text-slate-400 transition-transform ${dropdownOpen ? "rotate-180" : ""}`}
              />
            </button>

            {dropdownOpen && (
              <div className="absolute z-50 mt-1 w-full bg-white border border-slate-200 rounded-xl shadow-xl overflow-hidden">
                {/* Search inside dropdown */}
                <div className="p-2 border-b border-slate-100 flex items-center gap-2">
                  <Search size={13} className="text-slate-400 shrink-0" />
                  <input
                    autoFocus
                    type="text"
                    placeholder="Type name or ID..."
                    value={empSearch}
                    onChange={(e) => setEmpSearch(e.target.value)}
                    className="flex-1 text-[13px] text-slate-700 placeholder:text-slate-400 outline-none bg-transparent"
                  />
                </div>
                <div className="max-h-48 overflow-y-auto">
                  {empOptions.length === 0 ? (
                    <p className="text-center text-[12px] text-slate-400 py-4">No employees found</p>
                  ) : (
                    empOptions.map((emp) => {
                      // Check if this employee already has a shift to show a badge
                      const hasShift = (shifts || []).some(
                        (s) => s.employeeId === (emp.employeeId || emp.id)
                      );
                      return (
                        <button
                          key={emp._id || emp.id}
                          type="button"
                          onClick={() => handleSelectEmployee(emp)}
                          className="w-full text-left px-3 py-2.5 hover:bg-blue-50 transition-colors flex items-center gap-3 group"
                        >
                          <div className="w-7 h-7 rounded-lg bg-blue-100 text-blue-700 flex items-center justify-center text-[11px] font-bold shrink-0 group-hover:bg-blue-200 transition-colors">
                            {emp.name?.charAt(0)?.toUpperCase() || "?"}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-[13px] font-semibold text-slate-800">{emp.name}</p>
                            <p className="text-[11px] text-slate-400">
                              {emp.employeeId || emp.id} · {emp.designation || emp.role || "—"}
                            </p>
                          </div>
                          {hasShift && (
                            <span className="text-[10px] font-semibold text-amber-600 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded-full shrink-0">
                              Has Shift
                            </span>
                          )}
                        </button>
                      );
                    })
                  )}
                </div>
              </div>
            )}
            {errors.employeeId && (
              <p className="text-[11px] text-red-500 mt-1">{errors.employeeId}</p>
            )}
          </div>

          {/* Existing shift warning banner */}
          {isUpdateMode && (
            <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
              <AlertCircle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
              <div>
                <p className="text-[12px] font-semibold text-amber-800">
                  Existing shift detected
                </p>
                <p className="text-[11px] text-amber-700 mt-0.5">
                  {shiftEmoji}{" "}
                  <span className="capitalize font-medium">{existingShift.shift}</span> —{" "}
                  {formatTime(existingShift.startTime)} to {formatTime(existingShift.endTime)}.
                  The form has been pre-filled. Save to update.
                </p>
              </div>
            </div>
          )}

          {/* Shift Type */}
          <div>
            <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-2 block">
              Shift <span className="text-red-500">*</span>
            </label>
            <div className="grid grid-cols-3 gap-2">
              {SHIFT_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setField("shift", opt.value)}
                  className={`flex flex-col items-center gap-1 py-3 px-2 rounded-xl border-2 transition-all text-center ${
                    form.shift === opt.value
                      ? "border-blue-500 bg-blue-50 shadow-sm shadow-blue-100"
                      : "border-slate-200 bg-white hover:border-blue-300 hover:bg-blue-50/50"
                  }`}
                >
                  <span className="text-xl">{opt.icon}</span>
                  <span className={`text-[12px] font-semibold ${form.shift === opt.value ? "text-blue-700" : "text-slate-700"}`}>
                    {opt.label}
                  </span>
                  <span className="text-[10px] text-slate-400">{opt.desc}</span>
                </button>
              ))}
            </div>
            {errors.shift && <p className="text-[11px] text-red-500 mt-1">{errors.shift}</p>}
          </div>

          {/* Start & End Time */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5 block">
                Start Time <span className="text-red-500">*</span>
              </label>
              <input
                type="time"
                value={form.startTime}
                onChange={(e) => setField("startTime", e.target.value)}
                className={`w-full px-3 py-2.5 rounded-lg border text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-400 transition-all ${
                  errors.startTime ? "border-red-400 bg-red-50" : "border-slate-200 bg-white hover:border-blue-300"
                }`}
              />
              {errors.startTime && (
                <p className="text-[11px] text-red-500 mt-1">{errors.startTime}</p>
              )}
            </div>
            <div>
              <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5 block">
                End Time <span className="text-red-500">*</span>
              </label>
              <input
                type="time"
                value={form.endTime}
                onChange={(e) => setField("endTime", e.target.value)}
                className={`w-full px-3 py-2.5 rounded-lg border text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-400 transition-all ${
                  errors.endTime ? "border-red-400 bg-red-50" : "border-slate-200 bg-white hover:border-blue-300"
                }`}
              />
              {errors.endTime && (
                <p className="text-[11px] text-red-500 mt-1">{errors.endTime}</p>
              )}
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5 block">
              Notes <span className="text-slate-400 font-normal normal-case">(optional)</span>
            </label>
            <textarea
              rows={3}
              value={form.notes}
              onChange={(e) => setField("notes", e.target.value)}
              placeholder="Any additional details about this shift..."
              className="w-full px-3 py-2.5 rounded-lg border border-slate-200 bg-white text-sm text-slate-700 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-400 hover:border-blue-300 transition-all resize-none"
            />
          </div>

        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-100 bg-slate-50/50 flex justify-end gap-3 flex-shrink-0">
          <button
            type="button"
            onClick={handleClose}
            className="h-9 px-4 rounded-lg text-[13px] font-semibold text-slate-600 bg-white border border-slate-200 hover:bg-slate-50 hover:text-slate-800 transition-all"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={isSaving}
            className={`h-9 px-5 rounded-lg text-[13px] font-semibold text-white shadow-sm hover:scale-105 active:scale-95 transition-all disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:scale-100 ${
              isUpdateMode
                ? "bg-amber-500 hover:bg-amber-600 shadow-amber-200"
                : "bg-blue-600 hover:bg-blue-700 shadow-blue-200"
            }`}
          >
            {isSaving
              ? "Saving..."
              : isUpdateMode
              ? "Update Shift"
              : "Assign Shift"}
          </button>
        </div>
      </div>
    </div>
  );
}
